import { DialogComponent } from '@theme/dialog';

const KLAVIYO_REVISION = '2024-06-15';

/**
 * The "Notify me when back in stock" form. Any `[data-notify-me="<variant id>"]` button on the
 * page opens it; submitting signs the shopper up with Klaviyo for that exact variant, so it
 * works where Klaviyo's own button can't (the bundle builder, the trade quick order list).
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog
 * @property {HTMLFormElement} form
 * @property {HTMLElement} item - Names what the shopper is signing up for.
 * @property {HTMLInputElement} email
 * @property {HTMLInputElement} [newsletter]
 * @property {HTMLButtonElement} submit
 * @property {HTMLElement} error
 * @property {HTMLElement} done
 *
 * @extends DialogComponent
 */
class NotifyMeDialog extends DialogComponent {
  requiredRefs = ['dialog', 'form', 'item', 'email', 'submit', 'error', 'done'];

  #variantId = '';

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener('click', this.#handleTriggerClick);
    this.refs.form.addEventListener('submit', this.#handleSubmit);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener('click', this.#handleTriggerClick);
  }

  /** @param {MouseEvent} event */
  #handleTriggerClick = (event) => {
    const trigger = event.target instanceof Element ? event.target.closest('[data-notify-me]') : null;
    if (!(trigger instanceof HTMLElement) || !trigger.dataset.notifyMe) return;
    event.preventDefault();
    event.stopPropagation();
    this.open(trigger.dataset.notifyMe, trigger.dataset.notifyMeName || '');
  };

  /**
   * @param {string} variantId
   * @param {string} name
   */
  open(variantId, name) {
    const { form, item, error, done, submit } = this.refs;
    this.#variantId = variantId;
    item.textContent = name;
    form.hidden = false;
    done.hidden = true;
    error.hidden = true;
    submit.disabled = false;
    this.showDialog();
  }

  /** @param {SubmitEvent} event */
  #handleSubmit = async (event) => {
    event.preventDefault();
    const { email, newsletter, submit, error, form, done } = this.refs;
    if (!email.reportValidity()) return;

    submit.disabled = true;
    error.hidden = true;
    try {
      await this.#post('back-in-stock-subscriptions', {
        type: 'back-in-stock-subscription',
        attributes: {
          channels: ['EMAIL'],
          profile: { data: { type: 'profile', attributes: { email: email.value } } },
        },
        relationships: {
          variant: { data: { type: 'catalog-variant', id: `$shopify:::$default:::${this.#variantId}` } },
        },
      });
      if (newsletter?.checked && this.dataset.listId) {
        // The restock sign-up already succeeded; a failed newsletter opt-in shouldn't undo it.
        await this.#post('subscriptions', {
          type: 'subscription',
          attributes: {
            profile: {
              data: {
                type: 'profile',
                attributes: { email: email.value, subscriptions: { email: { marketing: { consent: 'SUBSCRIBED' } } } },
              },
            },
          },
          relationships: { list: { data: { type: 'list', id: this.dataset.listId } } },
        }).catch(() => {});
      }
      form.hidden = true;
      done.hidden = false;
    } catch {
      error.hidden = false;
      submit.disabled = false;
    }
  };

  /**
   * @param {string} endpoint
   * @param {object} data
   */
  async #post(endpoint, data) {
    const company = encodeURIComponent(this.dataset.companyId || '');
    const response = await fetch(`https://a.klaviyo.com/client/${endpoint}/?company_id=${company}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', revision: KLAVIYO_REVISION },
      body: JSON.stringify({ data }),
    });
    if (!response.ok) throw new Error(`Klaviyo ${endpoint}: ${response.status}`);
  }
}

if (!customElements.get('notify-me-dialog')) customElements.define('notify-me-dialog', NotifyMeDialog);
